import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", society_name: "", city: "", approx_flats: "", message: "" };

export function DemoRequestDialog({ open, onOpenChange }) {
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    function extractErrorMessage(err, fallback) {
        const detail = err?.response?.data?.detail;
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail) && detail.length) return detail.map((d) => d.msg || String(d)).join(" ");
        return fallback;
    }

    async function submit(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.society_name.trim() || !form.email.trim()) {
            toast.error("Please fill in your name, phone, email and society/community name");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/demo-requests`, form);
            setDone(true);
        } catch (err) {
            toast.error(extractErrorMessage(err, "Something went wrong. Please try again."));
        } finally {
            setSubmitting(false);
        }
    }

    function handleClose(next) {
        onOpenChange(next);
        if (!next) setTimeout(() => { setForm(EMPTY); setDone(false); }, 200);
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="demo-request-dialog">
                {done ? (
                    <div className="text-center py-8" data-testid="demo-request-success">
                        <div className="w-14 h-14 rounded-full bg-[#ECFDF5] flex items-center justify-center mx-auto mb-4">
                            <CheckCircle size={28} weight="fill" className="text-[#059669]" />
                        </div>
                        <h3 className="font-heading font-semibold text-2xl text-brand-ink mb-2">Thanks — request received!</h3>
                        <p className="text-brand-inkSoft">Our team will reach out shortly to schedule your Maintayn walkthrough.</p>
                        <Button onClick={() => handleClose(false)} className="mt-6 rounded-full bg-brand-action hover:bg-brand-actionHover text-white" data-testid="demo-request-close-btn">
                            Close
                        </Button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-heading font-bold text-2xl text-brand-ink">Book a Demo</DialogTitle>
                            <DialogDescription>Tell us about your community — we'll set up a personalised walkthrough.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submit} noValidate className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label>Your Name *</Label>
                                    <Input data-testid="demo-request-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Phone *</Label>
                                    <Input data-testid="demo-request-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Email *</Label>
                                <Input data-testid="demo-request-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Society / Community Name *</Label>
                                <Input data-testid="demo-request-society" value={form.society_name} onChange={(e) => set("society_name", e.target.value)} placeholder="Green Valley Residency" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label>City</Label>
                                    <Input data-testid="demo-request-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Pune" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Approx. Number of Flats</Label>
                                    <Input data-testid="demo-request-flats" value={form.approx_flats} onChange={(e) => set("approx_flats", e.target.value)} placeholder="120" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>What are you looking for?</Label>
                                <Textarea data-testid="demo-request-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us a bit about your community's needs..." />
                            </div>
                            <Button type="submit" disabled={submitting} data-testid="demo-request-submit" className="w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white py-6 text-base font-semibold">
                                {submitting ? "Sending..." : <>Request My Demo <PaperPlaneTilt size={16} className="ml-2" /></>}
                            </Button>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
